import { type ComponentType, lazy, type ReactElement, Suspense } from 'react';
import { CarregandoTelaCheia } from '@/componentes/CarregandoTriplice';

/**
 * O `element` de uma rota cuja página só é baixada quando alguém a abre (divisão do bundle, iteração 1.12
 * do Usuários — o painel saía num pedaço só de 707 KB).
 *
 * É o mesmo desenho da rota do plano estratégico (`React.lazy` + `Suspense`, e não o `lazy` do objeto de
 * rota): o `fallback` é o loader da marca e aparece também na navegação interna, não só na primeira carga
 * (CONVENCOES-UI §6 — nunca spinner genérico).
 *
 * `escolher` existe porque as páginas são exportações **nomeadas** (o padrão do painel); o `React.lazy`
 * exige um `default`, e trocar o export de cada página só para o carregamento seria acoplar a página a
 * como ela é carregada.
 *
 * 🚨 Chame no **nível do módulo** (dentro do array de rotas), nunca durante o render: cada chamada cria um
 * componente novo, e criá-lo a cada render faria a página ser desmontada e baixada de novo.
 *
 * Se o pedaço não chegar (um deploy trocou o nome do arquivo enquanto a aba estava aberta), o erro sobe ao
 * `errorElement` da rota — a `PaginaErro`, que oferece recarregar.
 */
export function paginaSobDemanda<M>(
  carregar: () => Promise<M>,
  escolher: (modulo: M) => ComponentType,
  rotulo = 'Abrindo a página…',
): ReactElement {
  const Pagina = lazy(async () => ({ default: escolher(await carregar()) }));
  return (
    <Suspense fallback={<CarregandoTelaCheia rotulo={rotulo} />}>
      <Pagina />
    </Suspense>
  );
}
